import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../../components/cancelButton/CancelButton";

export interface RestoreProductDialogItem {
  readonly index: number;
  readonly name: string;
  readonly date: string;
}

interface Props {
  readonly open: boolean;
  readonly products: RestoreProductDialogItem[];
  readonly onConfirm: (selectedIndexes: number[]) => Promise<void>;
  readonly onCancel: () => void;
}

const RestoreProductDialog: FC<Props> = ({ open, products, onConfirm, onCancel }) => {
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open && products.length > 0) {
      setSelectedIndexes(new Set(products.map((p) => p.index)));
    }
  }, [open, products]);

  const handleToggle = (index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIndexes(checked ? new Set(products.map((p) => p.index)) : new Set());
  };

  const handleConfirm = () => onConfirm([...selectedIndexes].sort((a, b) => a - b));

  const allSelected = products.length > 0 && selectedIndexes.size === products.length;
  const someSelected = selectedIndexes.size > 0;

  return (
    <Dialog open={open} maxWidth="lg">
      <DialogTitle>Cofnij anulowanie produktów</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            marginTop: 1,
          }}
        >
          <Typography>
            Czy na pewno chcesz cofnąć anulowanie tego produktu? Jeśli tak, wybierz produkty z tej samej rezerwacji, dla
            których anulowanie ma zostać cofnięte:
          </Typography>
          {products.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(_, checked) => handleSelectAll(checked)}
                      aria-label="Zaznacz wszystkie"
                    />
                  </TableCell>
                  <TableCell>Produkt</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map(({ index, name, date }) => (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndexes.has(index)}
                        onChange={() => handleToggle(index)}
                        aria-label={`Zaznacz ${name}`}
                      />
                    </TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Alert severity="info">
            Jeśli rezerwacja została już anulowana u dostawcy, produkt wróci bez numeru rezerwacji i trzeba go
            zarezerwować ponownie.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onCancel} />
        <AsyncLoadingButton onClick={handleConfirm} disabled={!someSelected}>
          Potwierdź przywrócenie
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RestoreProductDialog;
